import { Document, Font, Text, View } from '@react-pdf/renderer';

import type { Scan } from '../../../api/generated/insights.schemas';
import PageShell from './PageShell';
import CitationsSection from './sections/CitationsSection';
import CompetitorRankingSection from './sections/CompetitorRankingSection';
import CoverPage from './sections/CoverPage';
import MentionsSection from './sections/MentionsSection';
import SentimentSection from './sections/SentimentSection';
import VisibilitySection from './sections/VisibilitySection';
import { styles } from './styles';

// `@react-pdf/renderer` only breaks lines at whitespace by default, so long
// unbreakable strings — typically citation URLs — overflow into adjacent
// table cells. Registering a hyphenation callback gives the layout engine a
// break opportunity at every character of any "word" longer than 16 chars.
// The engine still packs as many characters onto a line as fit, so normal
// text is unaffected — only oversize tokens like URLs end up wrapping.
Font.registerHyphenationCallback((word) =>
  word.length > 16 ? Array.from(word) : [word],
);

export interface BrandAnalysisPdfDocumentProps {
  brandName: string;
  aiProviderLabel: string;
  analysisDate: string;
  generatedAt: string;
  scan: Scan;
  radarImage: string;
  mentionsProgressImage: string | null;
}

export default function BrandAnalysisPdfDocument({
  brandName,
  aiProviderLabel,
  analysisDate,
  generatedAt,
  scan,
  radarImage,
  mentionsProgressImage,
}: BrandAnalysisPdfDocumentProps) {
  const hasMentions =
    (scan.mentions?.queriesWithMentions?.length ?? 0) > 0 ||
    (scan.mentions?.mentionedInNumberOfQueries?.value ?? 0) > 0;

  return (
    <Document
      title={`Brand analysis — ${brandName}`}
      author="Yoast AI Brand Insights"
      subject={`Brand analysis report for ${brandName} on ${aiProviderLabel}`}
    >
      <CoverPage
        brandName={brandName}
        aiProviderLabel={aiProviderLabel}
        analysisDate={analysisDate}
        generatedAt={generatedAt}
      />

      <PageShell brandName={brandName} generatedAt={generatedAt}>
        <VisibilitySection scan={scan} radarImage={radarImage} />
      </PageShell>

      {hasMentions ? (
        <>
          <PageShell brandName={brandName} generatedAt={generatedAt}>
            <MentionsSection
              scan={scan}
              mentionsProgressImage={mentionsProgressImage}
            />
          </PageShell>

          <PageShell brandName={brandName} generatedAt={generatedAt}>
            <CompetitorRankingSection scan={scan} />
          </PageShell>

          <PageShell brandName={brandName} generatedAt={generatedAt}>
            <CitationsSection scan={scan} />
          </PageShell>

          <PageShell brandName={brandName} generatedAt={generatedAt}>
            <SentimentSection
              ownSentiment={scan.sentiment?.own}
              competitorSentiment={scan.sentiment?.topCompetitor}
            />
          </PageShell>
        </>
      ) : (
        <PageShell brandName={brandName} generatedAt={generatedAt}>
          <View>
            <Text style={styles.sectionTitle}>No mentions yet</Text>
            <Text style={styles.sectionSubtitle}>
              The selected AI platform did not mention the brand for any of the
              submitted queries on this analysis date. Consider broadening the
              query set or running the analysis again on a later date.
            </Text>
          </View>
        </PageShell>
      )}
    </Document>
  );
}
